import { useMemo } from "react";
import { Editor } from "@tiptap/react";
import { icons } from "lucide-react";

import { FieldToken } from "models";
import { Icon, SidebarMenu } from "utils/ui";
import FIELD_TOKENS from "pages/tip-tap-editor/extensions/FieldToken/groups";

type Chip = {
  key: string;
  label: string;
  title: string;
  icon?: keyof typeof icons;
  insert: (editor: Editor) => void;
};

type ChipGroup = {
  title: string;
  /** Field-token (mention) ids rendered as chips. */
  tokenIds?: FieldToken[];
  /** Extra non-token chips (e.g. auto-filled image blocks). */
  chips?: Chip[];
};

// Logo & Project Image are auto-filled from data (like fields), so they live in
// the Fields area rather than the Blocks list — they insert real image blocks.
const IMAGE_CHIPS: Chip[] = [
  {
    key: "logo",
    label: "Company Logo",
    title: "Insert your company logo (from company settings)",
    icon: "Image",
    insert: (editor) => editor.chain().focus().setLogoImageBlock().run(),
  },
  {
    key: "projectImage",
    label: "Project Image",
    title: "Insert the project image (from project settings)",
    icon: "Image",
    insert: (editor) => editor.chain().focus().setProjectImageBlock().run(),
  },
];

const GROUPS: ChipGroup[] = [
  { title: "Project", tokenIds: ["projectName", "projectRef", "projectAddress", "client", "createDate"] },
  { title: "Company", tokenIds: ["company"] },
  { title: "Images", chips: IMAGE_CHIPS },
  { title: "Report", tokenIds: ["userName", "userPosition", "reportNotes"] },
];

/**
 * Auto-filled tokens shown as click-to-insert chips in the panel's Insert mode.
 * Text fields persist as `mention` nodes; the Images group inserts logo/project
 * image blocks (also data-driven, so grouped here rather than in Blocks).
 */
export const FieldTokenMenu = ({ editor }: { editor: Editor }) => {
  const groups = useMemo<{ title: string; chips: Chip[] }[]>(() => {
    const tokensById = new Map(FIELD_TOKENS.map((t) => [t.id, t]));

    return GROUPS.map((group) => {
      const tokenChips: Chip[] = (group.tokenIds ?? [])
        .map((id) => tokensById.get(id))
        .filter((t): t is NonNullable<typeof t> => !!t)
        .map((t) => ({
          key: t.id,
          label: t.label,
          title: t.description,
          insert: (editor: Editor) =>
            editor.chain().focus().insertContent({ type: "mention", attrs: { id: t.id, label: t.label } }).run(),
        }));

      return { title: group.title, chips: [...tokenChips, ...(group.chips ?? [])] };
    }).filter((g) => g.chips.length > 0);
  }, []);

  return (
    <div className="mb-4">
      <SidebarMenu.Heading title="Fields" />
      <SidebarMenu.Description className="mb-2">Click to insert at cursor</SidebarMenu.Description>

      {groups.map((group) => (
        <div key={group.title} className="mb-3">
          <SidebarMenu.SubHeading className="mb-0" title={group.title} />
          <div className="flex flex-wrap gap-2">
            {group.chips.map((chip) => (
              <button
                key={`chip-${chip.key}`}
                type="button"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-[var(--primary-700)] bg-[var(--primary-50)] border border-[var(--primary-200)] rounded-md py-1 px-2.5 cursor-pointer hover:bg-[var(--primary-100)]"
                title={chip.title}
                onClick={() => chip.insert(editor)}
              >
                {chip.icon ? <Icon name={chip.icon} size={12} strokeWidth={2} /> : null}
                {chip.label}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
